import { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  Search,
  ChevronDown,
  Info,
  Wallet,
  Package,
  DollarSign,
  Trophy,
  Shield,
} from "lucide-react";
import { faqCategories } from "./FAQData";

const ICONS = {
  Info,
  Wallet,
  Package,
  DollarSign,
  Trophy,
  Shield,
};

export default function FAQ() {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategory, setExpandedCategory] = useState(null);

  const handleCategoryChange = (category) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  const filteredCategories = faqCategories
    .map((category) => ({
      ...category,
      faqs: category.faqs.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    }))
    .filter((category) => category.faqs.length > 0);

  return (
    <Box
      id="faqs"
      sx={{
        py: { xs: 8, md: 12 },
        background: "linear-gradient(180deg, #101010 0%, #1e1e1e 100%)",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)",
        },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            textAlign: "center",
            mb: { xs: 6, md: 8 },
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: -20,
              left: "50%",
              transform: "translateX(-50%)",
              width: "1px",
              height: "40px",
              background:
                "linear-gradient(to bottom, rgba(0,173,181,0.5), transparent)",
            },
          }}
        >
          <Typography
            variant="h2"
            sx={{
              mb: 3,
              fontSize: { xs: "2rem", sm: "2.25rem", md: "2.5rem" },
              fontWeight: 800,
              position: "relative",
              display: "inline-block",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -12,
                left: "50%",
                transform: "translateX(-50%)",
                width: 100,
                height: 4,
                background:
                  "linear-gradient(90deg, transparent, #00ADB5, transparent)",
                borderRadius: "2px",
              },
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "text.secondary",
              maxWidth: "700px",
              mx: "auto",
              fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
              lineHeight: 1.6,
              px: { xs: 2, sm: 0 },
            }}
          >
            Find answers to common questions about RSC's services and features
          </Typography>
        </Box>

        {/* Search Bar */}
        <Box
          sx={{
            mb: { xs: 5, md: 6 },
            maxWidth: "600px",
            mx: "auto",
            px: { xs: 2, sm: 4, md: 0 },
            position: "relative",
          }}
        >
          <TextField
            fullWidth
            placeholder="Search questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <Search
                  size={20}
                  style={{ marginRight: "12px", color: "#00ADB5" }}
                />
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "56px",
                borderRadius: "28px",
                bgcolor: "rgba(0,0,0,0.3)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  bgcolor: "rgba(0,0,0,0.4)",
                  border: "1px solid rgba(0,173,181,0.3)",
                  boxShadow: "0 0 20px rgba(0,173,181,0.1)",
                },
                "&.Mui-focused": {
                  bgcolor: "rgba(0,0,0,0.5)",
                  border: "1px solid #00ADB5",
                  boxShadow: "0 0 25px rgba(0,173,181,0.2)",
                },
              },
              "& .MuiOutlinedInput-input": {
                fontSize: "1rem",
                fontWeight: 500,
                color: "white",
                "&::placeholder": {
                  color: "rgba(255,255,255,0.5)",
                  opacity: 1,
                },
              },
            }}
          />
        </Box>

        {/* FAQ Categories */}
        <Box sx={{ mx: "auto", px: { xs: 2, sm: 4, md: 0 } }}>
          {filteredCategories.map((category, index) => {
            const IconComponent = ICONS[category.icon];
            return (
              <Box
                key={index}
                sx={{
                  mb: 4,
                  background:
                    "linear-gradient(145deg, rgba(30,30,30,0.9), rgba(16,16,16,0.95))",
                  backdropFilter: "blur(10px)",
                  borderRadius: "20px",
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.1)",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
                    border: "1px solid rgba(0,173,181,0.3)",
                  },
                }}
              >
                <Box
                  onClick={() => handleCategoryChange(category.title)}
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    borderBottom:
                      expandedCategory === category.title
                        ? "1px solid rgba(255,255,255,0.1)"
                        : "none",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      bgcolor: "rgba(0,173,181,0.1)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      mr: 2.5,
                      color: "#00ADB5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: { xs: 45, sm: 50 },
                      height: { xs: 45, sm: 50 },
                      borderRadius: "15px",
                      bgcolor: "rgba(0,173,181,0.1)",
                      transition: "all 0.3s ease",
                      transform:
                        expandedCategory === category.title
                          ? "scale(1.1)"
                          : "scale(1)",
                    }}
                  >
                    <IconComponent size={26} />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      flex: 1,
                      color:
                        expandedCategory === category.title
                          ? "#00ADB5"
                          : "white",
                      fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.25rem" },
                      fontWeight: 600,
                      transition: "all 0.3s ease",
                    }}
                  >
                    {category.title}
                  </Typography>
                  <ChevronDown
                    size={22}
                    style={{
                      transform:
                        expandedCategory === category.title
                          ? "rotate(180deg)"
                          : "rotate(0)",
                      transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      color:
                        expandedCategory === category.title
                          ? "#00ADB5"
                          : "white",
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display:
                      expandedCategory === category.title ? "block" : "none",
                    p: { xs: 2, sm: 3 },
                  }}
                >
                  {category.faqs.map((faq, faqIndex) => (
                    <Accordion
                      key={faqIndex}
                      elevation={0}
                      sx={{
                        bgcolor: "transparent",
                        "&:before": { display: "none" },
                        "& .MuiAccordionSummary-root": {
                          minHeight: "60px",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            color: "#00ADB5",
                          },
                        },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ChevronDown size={18} />}
                        sx={{
                          px: 2,
                          "& .MuiAccordionSummary-expandIconWrapper": {
                            color: "#00ADB5",
                            transition: "transform 0.3s ease",
                            "&.Mui-expanded": {
                              transform: "rotate(180deg)",
                            },
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: { xs: "0.95rem", sm: "1rem" },
                            lineHeight: 1.5,
                            pr: 2,
                          }}
                        >
                          {faq.question}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          px: 2,
                          py: 2.5,
                          bgcolor: "rgba(0,0,0,0.2)",
                          borderRadius: "12px",
                          margin: "0 8px 12px 8px",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "text.secondary",
                            whiteSpace: "pre-line",
                            lineHeight: 1.7,
                            fontSize: { xs: "0.9rem", sm: "0.95rem" },
                          }}
                        >
                          {faq.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
}
